import React from 'react';
import { View, StyleSheet, ActivityIndicator } from 'react-native';
import { Text } from '@/components/common/text';
import { useThemeColors, useThemedStyles } from '@/hooks/residents/useThemeColors';
import { PressableScale } from '@/components/shared/redesign/PressableScale';

interface ChatCTACardProps {
  onPress?: () => void;
  variant?: 'mobile' | 'desktop';
  loading?: boolean;
}

// Chat Icon Component
const ChatIcon: React.FC<{ borderColor: string }> = ({ borderColor }) => (
  <View style={staticStyles.chatIcon}>
    <View style={[staticStyles.chatBubble, { borderColor }]} />
  </View>
);

export const ChatCTACard: React.FC<ChatCTACardProps> = ({
  onPress,
  variant = 'mobile',
  loading = false,
}) => {
  const { components } = useThemeColors();
  const styles = useChatCTAStyles();

  return (
    <View style={[styles.card, variant === 'desktop' && styles.desktopCard]}>
      <View style={staticStyles.textContainer}>
        <Text style={styles.ctaTitle}>You've got questions?</Text>
        <Text style={styles.ctaSubtitle}>Chat with the community</Text>
      </View>
      <PressableScale
        hover="button"
        style={({ pressed }) => [styles.button, pressed ? staticStyles.pressed : null]}
        onPress={onPress}
        disabled={loading}
        accessibilityRole="button"
      >
        {loading ? (
          <ActivityIndicator
            size="small"
            color={components.button.primaryText}
          />
        ) : (
          <>
            <ChatIcon borderColor={components.button.primaryText} />
            <Text style={styles.buttonText}>Start chat</Text>
          </>
        )}
      </PressableScale>
    </View>
  );
};

// Static styles that don't depend on theme
const staticStyles = StyleSheet.create({
  textContainer: {
    flex: 1,
    gap: 2,
  },
  pressed: {
    opacity: 0.85,
  },
  chatIcon: {
    width: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chatBubble: {
    width: 14,
    height: 12,
    borderWidth: 1.5,
    borderRadius: 6,
    borderBottomLeftRadius: 2,
  },
});

const useChatCTAStyles = () =>
  useThemedStyles(({ colors, semantic, components }) => ({
    card: {
      backgroundColor: colors.card.bg,
      borderRadius: 16,
      padding: 20,
      // Hairline matches the GlassCards above it on the mobile detail screen
      // (the desktop override below already carried its own border).
      borderWidth: 1,
      borderColor: colors.card.border,
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      justifyContent: 'space-between' as const,
      gap: 12,
    },
    desktopCard: {
      marginHorizontal: 24,
      // The desktop detail panel's background IS colors.card.bg, so the card's
      // default card.bg blends into it. Use the app's base surface + a hairline
      // border so the CTA reads as a distinct card in both light and dark.
      backgroundColor: colors.bg.base,
      borderWidth: 1,
      borderColor: colors.card.border,
    },
    ctaTitle: {
      fontSize: 14,
      fontWeight: '500' as const,
      color: colors.text.primary,
      lineHeight: 21,
    },
    ctaSubtitle: {
      fontSize: 12,
      fontWeight: '300' as const,
      color: colors.text.muted,
      lineHeight: 18,
    },
    button: {
      backgroundColor: semantic.interactive.primary,
      borderRadius: 8,
      paddingHorizontal: 16,
      paddingVertical: 8,
      minHeight: 34, // 8 + 18 line + 8: exact at 1x, grows with the font
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      gap: 6,
    },
    buttonText: {
      fontSize: 12,
      fontWeight: '500' as const,
      color: components.button.primaryText,
      lineHeight: 18,
    },
  }));
